import React, { useState, useRef, useEffect } from 'react';
import { useWorkflow } from '../contexts/WorkflowContext';
import './WorkflowNode.css';

const WorkflowNode = ({ 
  node, 
  isSelected, 
  onClick, 
  onSettings, 
  onOutput, 
  onConnectionStart, 
  onConnectionEnd,
  isConnecting,
  connectionStart
}) => {
  const { updateNode, deleteNode } = useWorkflow();
  const [isDragging, setIsDragging] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const nodeRef = useRef(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  const getNodeIcon = (type) => {
    const icons = {
      'text_prompt': '📝',
      'image_preview': '🖼️',
      'video_preview': '🎬',
      'audio_preview': '🎵',
      'openai_gpt4': '🤖',
      'anthropic_claude': '🧠',
      'elevenlabs_tts': '🎤',
      'stable_video': '🎬',
      'runway_gen': '🎭',
      'pika_labs': '⚡'
    };
    return icons[type] || '🔧';
  };

  const getNodeColor = (type) => {
    if (type.includes('preview')) return '#28a745';
    if (type.includes('gpt4') || type.includes('claude')) return '#007bff';
    if (type.includes('tts') || type.includes('video')) return '#ffc107';
    return '#6c757d';
  };

  // Handle mouse events for dragging
  const handleMouseDown = (e) => {
    if (e.button === 0 && !e.target.closest('.node-action-btn, .input-connector, .output-connector')) { // Left click only, not on buttons or connectors
      setIsDragging(true);
      setIsMoving(false);
      const rect = nodeRef.current.getBoundingClientRect();
      setDragStart({
        x: e.clientX,
        y: e.clientY
      });
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.stopPropagation();
      e.preventDefault();
    } else if (e.button === 2) { // Right click for context menu
      e.preventDefault();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setShowContextMenu(true);
    }
  };

  // Global mouse move handler
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging && nodeRef.current) {
        setIsMoving(true);
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        // Calculate new position
        const newX = (node.position?.x || 0) + deltaX;
        const newY = (node.position?.y || 0) + deltaY;
        
        // Update node position in real-time
        updateNode(node.id, { position: { x: newX, y: newY } });
        
        // Update drag start for next move
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (isDragging) {
        setIsDragging(false);
        // If it was a short drag, treat as click
        if (!isMoving) {
          onClick(node);
        }
        setIsMoving(false);
      }
    };

    const handleGlobalClick = (e) => {
      if (showContextMenu && !e.target.closest('.context-menu')) {
        setShowContextMenu(false);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    if (showContextMenu) {
      document.addEventListener('click', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [isDragging, dragStart, node, isMoving, onClick, updateNode, showContextMenu]);

  const handleConnectionStart = (e, outputId) => {
    e.stopPropagation();
    onConnectionStart(node.id, outputId);
  };

  const handleConnectionEnd = (e, inputId) => {
    e.stopPropagation();
    onConnectionEnd(node.id, inputId);
  };

  const handleSettings = (e) => {
    e.stopPropagation();
    onSettings();
  };

  const handleOutput = (e) => {
    e.stopPropagation();
    onOutput();
  };

  const handleDeleteNode = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete the "${node.type.replace(/_/g, ' ')}" node?`)) {
      deleteNode(node.id);
    }
    setShowContextMenu(false);
  };

  const handleDuplicateNode = (e) => {
    e.stopPropagation();
    // Would need addNode function from context
    console.log('Duplicate node:', node.id);
    setShowContextMenu(false);
  };

  const canConnect = isConnecting && connectionStart && connectionStart.nodeId !== node.id;

  return (
    <div
      ref={nodeRef}
      className={`workflow-node ${isSelected ? 'selected' : ''} ${canConnect ? 'can-connect' : ''}`}
      style={{
        left: node.position?.x || 0,
        top: node.position?.y || 0,
        borderColor: getNodeColor(node.type)
      }}
      onMouseDown={handleMouseDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Node Header */}
      <div className="node-header">
        <div className="node-icon" style={{ backgroundColor: getNodeColor(node.type) }}>
          {getNodeIcon(node.type)}
        </div>
        <div className="node-title">
          {node.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </div>
        <div className="node-actions">
          <button 
            className="node-action-btn" 
            onClick={handleSettings}
            title="Settings"
          >
            <i className="fas fa-cog"></i>
          </button>
          {node.output && (
            <button 
              className="node-action-btn" 
              onClick={handleOutput}
              title="View Output"
            >
              <i className="fas fa-eye"></i>
            </button>
          )}
          <button 
            className="node-action-btn delete-btn" 
            onClick={handleDeleteNode}
            title="Delete Node"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      {/* Node Status */}
      <div className={`node-status ${node.status}`}>
        <span className="status-indicator"></span>
        {node.status}
      </div>

      {/* Node Inputs */}
      <div className="node-inputs">
        {getNodeInputs(node.type).map(input => (
          <div 
            key={input.id}
            className={`node-input ${canConnect ? 'can-connect' : ''}`}
            onMouseUp={(e) => handleConnectionEnd(e, input.id)}
          >
            <div className="input-connector"></div>
            <span className="input-label">{input.label}</span>
          </div>
        ))}
      </div>

      {/* Node Body */}
      <div className="node-body">
        {renderNodeContent(node)}
      </div>

      {/* Node Outputs */}
      <div className="node-outputs">
        {getNodeOutputs(node.type).map(output => (
          <div 
            key={output.id}
            className={`node-output ${isConnecting ? 'can-connect' : ''}`}
            onMouseDown={(e) => handleConnectionStart(e, output.id)}
          >
            <span className="output-label">{output.label}</span>
            <div className="output-connector"></div>
          </div>
        ))}
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenuPos.y,
            left: contextMenuPos.x,
            zIndex: 1000
          }}
        >
          <button className="context-menu-item" onClick={handleSettings}>
            <i className="fas fa-cog"></i> Settings
          </button>
          {node.output && (
            <button className="context-menu-item" onClick={handleOutput}>
              <i className="fas fa-eye"></i> View Output
            </button>
          )}
          <button className="context-menu-item" onClick={handleDuplicateNode}>
            <i className="fas fa-copy"></i> Duplicate
          </button>
          <button className="context-menu-item delete-item" onClick={handleDeleteNode}>
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      )}
    </div>
  );
};

const getNodeInputs = (type) => {
  const inputs = {
    'text_prompt': [],
    'image_preview': [{ id: 'image', label: 'Image' }],
    'video_preview': [{ id: 'video', label: 'Video' }],
    'audio_preview': [{ id: 'audio', label: 'Audio' }],
    'openai_gpt4': [{ id: 'prompt', label: 'Prompt' }],
    'anthropic_claude': [{ id: 'prompt', label: 'Prompt' }],
    'elevenlabs_tts': [{ id: 'text', label: 'Text' }],
    'stable_video': [{ id: 'prompt', label: 'Prompt' }],
    'runway_gen': [{ id: 'prompt', label: 'Prompt' }],
    'pika_labs': [{ id: 'prompt', label: 'Prompt' }]
  };
  return inputs[type] || [];
};

const getNodeOutputs = (type) => {
  const outputs = {
    'text_prompt': [{ id: 'text', label: 'Text' }],
    'image_preview': [],
    'video_preview': [],
    'audio_preview': [],
    'openai_gpt4': [{ id: 'text', label: 'Generated Text' }],
    'anthropic_claude': [{ id: 'text', label: 'Generated Text' }],
    'elevenlabs_tts': [{ id: 'audio', label: 'Audio' }],
    'stable_video': [{ id: 'video', label: 'Video' }],
    'runway_gen': [{ id: 'video', label: 'Video' }],
    'pika_labs': [{ id: 'video', label: 'Video' }]
  };
  return outputs[type] || [];
};

const renderNodeContent = (node) => {
  switch (node.type) {
    case 'text_prompt':
      return (
        <div className="node-content">
          <textarea 
            placeholder="Enter your prompt here..."
            defaultValue={node.settings?.prompt || ''}
            readOnly
          />
        </div>
      );
    
    case 'image_preview':
      return (
        <div className="node-content">
          <div className="image-placeholder">
            <i className="fas fa-image"></i>
            <span>Image will appear here</span>
          </div>
        </div>
      );
    
    case 'video_preview':
      return (
        <div className="node-content">
          <div className="video-placeholder">
            <i className="fas fa-video"></i>
            <span>Video will appear here</span>
          </div>
        </div>
      );
    
    case 'audio_preview':
      return (
        <div className="node-content">
          <div className="audio-placeholder">
            <i className="fas fa-music"></i>
            <span>Audio will appear here</span>
          </div>
        </div>
      );
    
    default:
      return (
        <div className="node-content">
          <div className="ai-provider-info">
            <span>AI Provider</span>
            <small>Configure in settings</small>
          </div>
        </div>
      );
  }
};

export default WorkflowNode;
